import { isSignedViewPath } from '@/utils/imageDisplayAccess';
import {
  isViewLoadCooldownActive,
  waitForViewLoadCooldown,
} from '@/utils/viewLoadRateLimitGuard';

/** 默认并发（普通页）；大列表场景降到一半（C2：未配置 env 时 16 / 12） */
const DEFAULT_MAX_CONCURRENT = 16;
const LARGE_LIST_MAX_CONCURRENT = 12;

/** @returns {number|null} 配置的默认档并发；未设则 null */
function envConfiguredDefaultMax() {
  const raw = Number(process.env.REACT_APP_MANAGED_VIEW_MAX_CONCURRENT);
  return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : null;
}

/**
 * env 为默认档目标并发（可抬高，不再只当硬顶 Math.min）。
 * 大列表 = max(1, floor(default/2))，未配置时保持 16 / 12。
 */
function resolveMaxForProfile(profile) {
  const configured = envConfiguredDefaultMax();
  const defaultMax = configured ?? DEFAULT_MAX_CONCURRENT;
  if (profile === 'large-list') {
    if (configured == null) return LARGE_LIST_MAX_CONCURRENT;
    return Math.max(1, Math.floor(defaultMax / 2));
  }
  return defaultMax;
}

const state = {
  profile: 'default',
  largeListHolders: 0,
  max: resolveMaxForProfile('default'),
  active: 0,
  /** @type {{ scopeUrl: string, resolve: (slot: object) => void, cancelled: boolean }[]} */
  waiters: [],
  drainWaiters: [],
};

function notifyDrainWaitersIfIdle() {
  if (state.active > 0 || state.drainWaiters.length === 0) return;
  const pending = state.drainWaiters.splice(0, state.drainWaiters.length);
  pending.forEach((resolve) => resolve());
}

/**
 * 仅 HMAC `/api/storage/images/view` 进队列（防 429）。
 * OSS/CDN 预签名交给浏览器连接池：大列表若也排队，虚拟列表滚过后陈旧 waiter
 * 会堵死可见格，出现「接口有图、预览正常、列表白底」。
 */
function needsLoadSlot(url) {
  return isSignedViewPath(url);
}

function pumpQueue() {
  while (state.active < state.max && state.waiters.length > 0) {
    const idx = state.waiters.findIndex((w) => !w.cancelled && !isViewLoadCooldownActive(w.scopeUrl));
    if (idx < 0) return;
    const [next] = state.waiters.splice(idx, 1);
    if (next.cancelled) continue;
    state.active += 1;
    next.resolve({
      queued: true,
      cancelled: false,
      release: makeRelease(),
    });
  }
  notifyDrainWaitersIfIdle();
}

function applyProfileMax() {
  state.max = resolveMaxForProfile(state.profile);
  pumpQueue();
}

/**
 * 大列表场景（如图片空间虚拟列表）进入降并发模式；可嵌套，成对 leave。
 */
export function enterManagedViewLargeListMode() {
  state.largeListHolders += 1;
  state.profile = 'large-list';
  applyProfileMax();
}

export function leaveManagedViewLargeListMode() {
  state.largeListHolders = Math.max(0, state.largeListHolders - 1);
  if (state.largeListHolders === 0) {
    state.profile = 'default';
    applyProfileMax();
  }
}

export function getManagedViewLoadMaxConcurrent() {
  return state.max;
}

export function getManagedViewLoadProfile() {
  return state.profile;
}

/** @internal 测试 / 诊断：当前排队未取消数量 */
export function getManagedViewLoadWaiterCount() {
  return state.waiters.filter((w) => !w.cancelled).length;
}

/** 限流后等待当前已在途的 view 请求全部完成，再继续后续排队 */
export function waitForManagedViewActiveDrained() {
  if (state.active <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    state.drainWaiters.push(resolve);
  });
}

function makeRelease() {
  let released = false;
  return () => {
    if (released) return;
    released = true;
    state.active = Math.max(0, state.active - 1);
    notifyDrainWaitersIfIdle();
    pumpQueue();
  };
}

function makeCancelledSlot() {
  return {
    queued: false,
    cancelled: true,
    release() {},
  };
}

/**
 * 申请读图并发槽位；非 HMAC view 立即返回。
 * 支持 AbortSignal：卸载 / 滚出视口时取消排队，避免陈旧 waiter 堵死可见缩略图。
 * @param {string} viewUrl
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ release: () => void, queued: boolean, cancelled?: boolean }>}
 */
export async function acquireManagedViewLoadSlot(viewUrl, options = {}) {
  const url = String(viewUrl || '').trim();
  const signal = options.signal;
  if (!url || !needsLoadSlot(url)) {
    return { release() {}, queued: false, cancelled: false };
  }

  if (signal?.aborted) {
    return makeCancelledSlot();
  }

  if (isViewLoadCooldownActive(url)) {
    await waitForManagedViewActiveDrained();
    if (signal?.aborted) return makeCancelledSlot();
    await waitForViewLoadCooldown(url);
    if (signal?.aborted) return makeCancelledSlot();
  }

  if (state.active < state.max && !isViewLoadCooldownActive(url)) {
    state.active += 1;
    return {
      queued: true,
      cancelled: false,
      release: makeRelease(),
    };
  }

  return new Promise((resolve) => {
    /** @type {{ scopeUrl: string, resolve: (slot: object) => void, cancelled: boolean }} */
    const entry = {
      scopeUrl: url,
      cancelled: false,
      resolve,
    };

    const abort = () => {
      if (entry.cancelled) return;
      entry.cancelled = true;
      const idx = state.waiters.indexOf(entry);
      if (idx >= 0) state.waiters.splice(idx, 1);
      resolve(makeCancelledSlot());
      pumpQueue();
    };

    if (signal) {
      if (signal.aborted) {
        abort();
        return;
      }
      signal.addEventListener('abort', abort, { once: true });
    }

    state.waiters.push(entry);
    pumpQueue();
  });
}

export function isManagedViewLoadUrl(url) {
  return isSignedViewPath(url);
}

/** 冷却结束后由限流守卫调用，继续放行 FIFO 队列中未冷却的 scope */
export function resumeManagedViewLoadQueue() {
  pumpQueue();
}

/** @internal 测试用 */
export function __resetManagedViewLoadQueueForTests() {
  state.profile = 'default';
  state.largeListHolders = 0;
  state.max = resolveMaxForProfile('default');
  state.active = 0;
  state.waiters = [];
  state.drainWaiters = [];
}
